'use client';

import React, { useState, useMemo } from 'react';
import { Search, FlaskConical, Package, Check, ArrowRight, Zap, ChevronDown } from 'lucide-react';
import type { BookingData, SelectedService } from './BookingWizard';
import { ALL_TEST_RATES } from '../../data/testRates';

// Health packages — prices are package-level (not individual test rates)
const healthPackages = [
  { id: 'pkg-basic', name: 'Basic Health Check', price: 799, type: 'package' as const, tests: 35 },
  { id: 'pkg-complete', name: 'Complete Health Profile', price: 1499, type: 'package' as const, tests: 72 },
  { id: 'pkg-senior', name: 'Senior Citizen Care', price: 2199, type: 'package' as const, tests: 98 },
  { id: 'pkg-executive', name: 'Executive Health Checkup', price: 3499, type: 'package' as const, tests: 124 },
  { id: 'pkg-women', name: "Women\'s Health Package", price: 1799, type: 'package' as const, tests: 58 },
  { id: 'pkg-diabetes', name: 'Diabetes Care Package', price: 999, type: 'package' as const, tests: 28 },
  { id: 'pkg-heart', name: 'Cardiac Risk Assessment', price: 1299, type: 'package' as const, tests: 42 },
  { id: 'pkg-family', name: 'Family Health Package (4 members)', price: 4999, type: 'package' as const, tests: 72 },
];

const PAGE_SIZE = 20;

export default function Step1SelectService({
  bookingData,
  updateBooking,
  onNext,
}: {
  bookingData: BookingData;
  updateBooking: (u: Partial<BookingData>) => void;
  onNext: () => void;
}) {
  const [activeTab, setActiveTab] = useState<'tests' | 'packages'>('packages');
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Derive unique categories from all tests
  const categories = useMemo(() => {
    const cats = Array.from(new Set(ALL_TEST_RATES.map((t) => t.category ?? 'Other'))).sort();
    return ['All', ...cats];
  }, []);

  const filteredTests = useMemo(() => {
    return ALL_TEST_RATES.filter((t) => {
      const matchesSearch = t.name.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || t.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [search, selectedCategory]);

  const filteredPackages = useMemo(() =>
    healthPackages.filter((p) => p.name.toLowerCase().includes(search.toLowerCase())),
    [search]
  );

  const visibleTests = filteredTests.slice(0, visibleCount);

  const selectService = (item: SelectedService) => {
    updateBooking({ service: item });
  };

  const isSelected = (id: string) => bookingData.service?.id === id;

  // Reset visible count when search/category changes
  const handleSearch = (val: string) => {
    setSearch(val);
    setVisibleCount(PAGE_SIZE);
  };

  const handleCategory = (cat: string) => {
    setSelectedCategory(cat);
    setVisibleCount(PAGE_SIZE);
  };

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-6 py-5 border-b border-border">
        <h2 className="font-bold text-xl text-foreground">Select Test or Package</h2>
        <p className="text-muted-foreground text-sm mt-1">
          Choose from <span className="font-semibold text-primary">{ALL_TEST_RATES.length} diagnostic tests</span> or a comprehensive health package.
        </p>
      </div>

      <div className="p-6 space-y-5">
        {/* Search */}
        <div className="relative">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search tests or packages..."
            className="input-field pl-10"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2">
          <button
            className={`filter-tab flex items-center gap-1.5 ${activeTab === 'packages' ? 'active' : ''}`}
            onClick={() => setActiveTab('packages')}
          >
            <Package size={13} />
            Health Packages
          </button>
          <button
            className={`filter-tab flex items-center gap-1.5 ${activeTab === 'tests' ? 'active' : ''}`}
            onClick={() => setActiveTab('tests')}
          >
            <FlaskConical size={13} />
            Individual Tests ({ALL_TEST_RATES.length})
          </button>
        </div>

        {/* Packages list */}
        {activeTab === 'packages' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filteredPackages.map((pkg) => (
              <button
                key={pkg.id}
                onClick={() => selectService(pkg)}
                className={`text-left p-4 rounded-xl border-2 transition-all duration-150 ${
                  isSelected(pkg.id)
                    ? 'border-primary bg-secondary' :'border-border bg-card hover:border-primary/50 hover:bg-muted/50'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-sm text-foreground">{pkg.name}</h4>
                      {isSelected(pkg.id) && (
                        <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                          <Check size={11} className="text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mb-2">
                      <div className="flex items-center gap-1 bg-secondary rounded-md px-1.5 py-0.5">
                        <Zap size={10} className="text-primary" />
                        <span className="text-xs font-bold text-primary">{pkg.tests} Tests</span>
                      </div>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="stats-number text-lg font-extrabold text-primary">
                        ₹{pkg.price.toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Tests list */}
        {activeTab === 'tests' && (
          <div className="space-y-4">
            {/* Category filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground font-medium">Category:</span>
              <div className="flex items-center gap-1.5 flex-wrap">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => handleCategory(cat)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-all duration-150 ${
                      selectedCategory === cat
                        ? 'bg-primary text-white border-primary' :'bg-card text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Results count */}
            <p className="text-xs text-muted-foreground">
              Showing <span className="font-semibold text-foreground">{Math.min(visibleCount, filteredTests.length)}</span> of{' '}
              <span className="font-semibold text-foreground">{filteredTests.length}</span> tests
              {selectedCategory !== 'All' && ` in "${selectedCategory}"`}
            </p>

            {filteredTests.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <FlaskConical size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No tests found for &quot;{search}&quot;</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {visibleTests.map((test) => (
                    <button
                      key={test.id}
                      onClick={() => selectService({ id: test.id, name: test.name, price: test.rate, type: 'test' })}
                      className={`text-left p-4 rounded-xl border-2 transition-all duration-150 ${
                        isSelected(test.id)
                          ? 'border-primary bg-secondary' :'border-border bg-card hover:border-primary/50 hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2 mb-1">
                            <h4 className="font-semibold text-sm text-foreground leading-snug">{test.name}</h4>
                            {isSelected(test.id) && (
                              <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Check size={11} className="text-white" />
                              </div>
                            )}
                          </div>
                          {test.category && (
                            <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded-md">
                              {test.category}
                            </span>
                          )}
                          <div className="flex items-baseline gap-2 mt-1.5">
                            <span className="stats-number text-base font-extrabold text-primary">
                              ₹{test.rate.toLocaleString('en-IN')}
                            </span>
                          </div>
                        </div>
                        <FlaskConical size={16} className="text-muted-foreground flex-shrink-0" />
                      </div>
                    </button>
                  ))}
                </div>

                {/* Load more */}
                {visibleCount < filteredTests.length && (
                  <div className="flex justify-center pt-2">
                    <button
                      onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border bg-card hover:bg-muted/50 text-sm font-medium text-foreground transition-all duration-150"
                    >
                      <ChevronDown size={15} />
                      Load more ({Math.min(PAGE_SIZE, filteredTests.length - visibleCount)} more)
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Next button */}
        <div className="pt-4 border-t border-border flex justify-end">
          <button
            onClick={onNext}
            disabled={!bookingData.service}
            className="btn-primary"
          >
            Continue to Branch & Slot
            <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}